// TODO: This needs to be called from hook and nopt component file

/**
 * Calculate the progress percentage for warranty remaining
 * @param remaining - Remaining amount (months or miles)
 * @param total - Total amount
 * @returns Progress percentage (0-100) - 100% means full warranty remaining, 0% means expired
 */
export const calculateProgress = (remaining: number | null | undefined, total: number | null | undefined): number => {
    if (remaining == null || !total || total === 0) return 0;
    // Return 0 if expired (negative remaining)
    if (remaining < 0) return 0;
    // Calculate percentage of warranty remaining
    return Math.max(0, Math.min(100, (remaining / total) * 100));
};

/**
 * Format months into a human-readable string
 * @param months - Remaining months (positive for remaining, negative for expired)
 * @param total - Total months
 * @returns Formatted string (e.g., "2 yr, 9 mo left / 5 yrs", "0 mo left / 3 yrs", "Expired 5 mo ago / 3 yrs", or "3 yrs")
 */
export const formatMonths = (months: number | null | undefined, total: number | null | undefined): string => {
    if (months == null || !total) return 'N/A';
    const totalYears = Math.floor(total / 12);

    // Nothing consumed yet (NEW vehicle, or no in-service date) — the remaining/total framing is redundant
    if (months === total) return `${totalYears} ${totalYears === 1 ? 'yr' : 'yrs'}`;

    const absMonths = Math.abs(months);
    const years = Math.floor(absMonths / 12);
    const remainingMonths = absMonths % 12;

    let timeText = years > 0 ? `${years} yr` : '';
    if (remainingMonths > 0) {
        timeText += timeText ? `, ${remainingMonths} mo` : `${remainingMonths} mo`;
    }
    if (!timeText) timeText = '0 mo';

    const suffix = months < 0 ? ` ago / ${totalYears} yrs` : ` left / ${totalYears} yrs`;
    const prefix = months < 0 ? 'Expired ' : '';

    return `${prefix}${timeText}${suffix}`;
};

/**
 * Format miles into a human-readable string
 * @param miles - Remaining miles
 * @param total - Total miles
 * @returns Formatted string (e.g., "11k mi left / 60k mi", "60k mi", or "N/A")
 */
export const formatMiles = (miles: number | null | undefined, total: number | null | undefined): string => {
    // A missing limit is unknown, not unlimited — callers resolve unlimited coverage before formatting
    if (miles == null || !total) return 'N/A';

    const milesK = Math.abs(miles / 1000);
    const totalK = total / 1000;

    // Nothing consumed yet (NEW vehicle, or no odometer reading) — the remaining/total framing is redundant
    if (miles === total) return `${totalK}k mi`;

    if (miles < 0) {
        return `Expired ${milesK.toFixed(0)}k mi ago / ${totalK}k mi`;
    }
    return `${milesK.toFixed(0)}k mi left / ${totalK}k mi`;
};

/**
 * Format an in-service date for display
 * @param inServiceDate - Date string from the API (e.g., "2023-04-13")
 * @returns Long-form date (e.g., "April 13, 2023"), or the original string if it is not a valid date
 */
export const formatInServiceDate = (inServiceDate: string): string => {
    const parsed = new Date(inServiceDate);
    if (isNaN(parsed.getTime())) return inServiceDate;

    // Date-only strings parse as UTC midnight, so read the parts in UTC to avoid shifting a day back
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Calculate the number of filled dots for the progress indicator
 * @param progress - Progress percentage (0-100)
 * @param totalDots - Total number of dots to display
 * @returns Number of dots that should be filled
 */
export const calculateFilledDots = (progress: number, totalDots: number = 15): number => {
    return Math.round((progress / 100) * totalDots);
};
